import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from './cn.js';

const control =
  'h-10 rounded-sm border border-line bg-surface px-3 text-sm text-ink placeholder:text-ink-3 ' +
  'transition-colors duration-fast ease-od hover:border-gray-400 focus:border-azur-500 focus:outline-none ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azur-500 disabled:cursor-not-allowed disabled:opacity-45';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(control, !/(^|\s)w-/.test(className ?? '') && 'w-full', className)}
      {...rest}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(control, 'cursor-pointer pr-8', !/(^|\s)w-/.test(className ?? '') && 'w-full', className)}
      {...rest}
    >
      {children}
    </select>
  );
});

/** Champ de formulaire : libellé, contrôle, aide ou erreur. */
export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-[13px] font-semibold text-ink">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p role="alert" className="text-[13px] text-danger-700">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[13px] text-ink-3">{hint}</p>
      ) : null}
    </div>
  );
}
